// ============================================================================
// Line Official Account — error classes + wire shapes + sanitiser helper.
//
// Line's Messaging API returns success bodies with mostly-empty objects and
// errors as `{ message, details? }` — see
// https://developers.line.biz/en/reference/messaging-api/#error-responses.
//
// We classify the most common failure modes into a discriminated error class
// so callers (dispatcher, chat inbox, agents) can react without re-parsing
// Line's documented HTTP status matrix:
//
//   - AUTH_FAILED          → channel access token rejected (401). Instance
//                              needs re-auth.
//   - INVALID_REPLY_TOKEN  → the replyToken passed to /reply was unknown /
//                              already used. Caller should retry via /push.
//   - REPLY_TOKEN_EXPIRED  → 30s window passed. Same treatment as above.
//   - RATE_LIMITED         → 429. Retry after the `retry-after` hint (if any).
//   - USER_UNAVAILABLE     → the target user has blocked the OA / id unknown.
//   - INVALID_PAYLOAD      → 400 shape / body validation failed. Programmer
//                              error; fix caller.
//   - PROVIDER_ERROR       → everything else (5xx, network, unknown shape).
// ============================================================================

// ============================================================================
// Wire shapes — minimum slice of the Messaging API schema we care about.
// ============================================================================

/** `/v2/bot/message/reply` + `/push` respond with `{}` on success. */
export interface LineSendResponse {
  /** Line sometimes returns `sentMessages` for multicast; not used on reply/push. */
  sentMessages?: Array<{ id: string; quoteToken?: string }>
}

/** `GET /v2/bot/info` response (used by `validateChannelAccessToken`). */
export interface LineBotInfo {
  userId: string
  basicId?: string
  premiumId?: string
  displayName?: string
  pictureUrl?: string
  chatMode?: string
  markAsReadMode?: string
}

/** `GET /v2/bot/profile/{userId}` response. */
export interface LineUserProfileRaw {
  userId: string
  displayName: string
  pictureUrl?: string
  statusMessage?: string
  language?: string
}

/** Generic Line error envelope. */
export interface LineErrorEnvelope {
  message?: string
  details?: Array<{ message?: string; property?: string }>
}

// ============================================================================
// LineOaError — discriminated by `code` so callers can switch exhaustively.
// ============================================================================

export type LineOaErrorCode =
  | 'AUTH_FAILED'
  | 'INVALID_REPLY_TOKEN'
  | 'REPLY_TOKEN_EXPIRED'
  | 'RATE_LIMITED'
  | 'PROVIDER_ERROR'
  | 'INVALID_PAYLOAD'
  | 'USER_UNAVAILABLE'
  | 'INVALID_INPUT'

export interface LineOaErrorDetails {
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Raw provider message (sanitised). */
  providerMessage?: string
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
  /** Echoed details array from the envelope (first few entries, sanitised). */
  providerDetails?: Array<{ message?: string; property?: string }>
}

export class LineOaError extends Error {
  public readonly name = 'LineOaError'
  constructor(
    public readonly code: LineOaErrorCode,
    message: string,
    public readonly details: LineOaErrorDetails = {},
  ) {
    super(message)
  }
}

/** Dedicated error for signature verification — distinct class so tests can
 * match on `instanceof` without reaching for the string code. */
export class LineOaSignatureError extends Error {
  public readonly name = 'LineOaSignatureError'
  constructor(message: string) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / detect / sanitise
// ============================================================================

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseRetryAfterMs(headerValue: string | null | undefined): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

/**
 * Map an error envelope + HTTP status to a `LineOaError`.
 *
 * Decision tree:
 *   1. HTTP 401                                       → AUTH_FAILED
 *   2. HTTP 429                                       → RATE_LIMITED
 *   3. HTTP 400 with "reply token" in message        → REPLY_TOKEN_EXPIRED /
 *                                                        INVALID_REPLY_TOKEN
 *   4. HTTP 400                                       → INVALID_PAYLOAD
 *   5. HTTP 403 / 404 on profile/push                 → USER_UNAVAILABLE
 *   6. everything else                                → PROVIDER_ERROR
 */
export function classifyLineError(
  envelope: LineErrorEnvelope | null | undefined,
  httpStatus: number,
  retryAfterMs?: number,
): LineOaError {
  const rawMessage = envelope?.message ?? `Line Messaging API error (HTTP ${httpStatus})`
  const message = sanitizeLineErrorMessage(rawMessage)
  const details: LineOaErrorDetails = {
    httpStatus,
    providerMessage: message,
    retryAfterMs,
    providerDetails: Array.isArray(envelope?.details)
      ? envelope!.details!.slice(0, 8).map((d) => ({
          message: d.message ? sanitizeLineErrorMessage(d.message) : undefined,
          property: d.property,
        }))
      : undefined,
  }

  if (httpStatus === 401) {
    return new LineOaError('AUTH_FAILED', message, details)
  }
  if (httpStatus === 429) {
    return new LineOaError('RATE_LIMITED', message, details)
  }
  if (httpStatus === 400) {
    const lower = rawMessage.toLowerCase()
    // Line returns `Invalid reply token` OR `The reply token has already been used`
    // OR `The reply token expired` — match all three defensively.
    if (lower.includes('reply token')) {
      if (lower.includes('expire')) {
        return new LineOaError('REPLY_TOKEN_EXPIRED', message, details)
      }
      return new LineOaError('INVALID_REPLY_TOKEN', message, details)
    }
    return new LineOaError('INVALID_PAYLOAD', message, details)
  }
  // 404 on the profile / push path is a user-reachability issue (the OA is
  // not connected to this user id). 403 however is ambiguous — Line returns
  // 403 when the channel itself is suspended (ops problem), when the bot is
  // blocked by the user (user-reachability), or when the access token lost
  // scope (auth). Without disambiguation in the envelope we can't safely
  // narrow to `USER_UNAVAILABLE`, so 403 falls through to `PROVIDER_ERROR`
  // and the dashboard/runbook surfaces the raw message. (Round 1 H3 fix.)
  if (httpStatus === 404) {
    return new LineOaError('USER_UNAVAILABLE', message, details)
  }
  return new LineOaError('PROVIDER_ERROR', message, details)
}

/**
 * Strip Line-shaped secrets out of an error / log string before it leaves the
 * adapter. Line channel access tokens are long JWT-like base64 strings
 * (40+ chars, `[A-Za-z0-9+/=]` alphabet, sometimes starting with a short
 * header like `eyJ` or a plain random blob). We also scrub `Bearer ...`
 * prefixes universally.
 *
 * Patterns intentionally redact aggressively — false positives on an error
 * string are cheap; a leaked token in Workers tail logs is catastrophic.
 *
 * Issue #249 L1: the base64 catch-all below is intentionally aggressive and
 * will eat legitimate long strings (e.g. a rich error excerpt that happens to
 * contain a ~40-char alphanumeric blob). Consumers that need structured
 * diagnostics should read `error.details.providerDetails` (the parsed
 * envelope's per-property array, preserved with Line's `property` keys) or
 * tap the raw HTTP response before it reaches this function. Do NOT rely on
 * `error.message` for anything past a human-readable summary.
 */
export function sanitizeLineErrorMessage(raw: string): string {
  let out = raw
  // `Authorization: Bearer <token>` / `Bearer <token>` — any alphanum+punct.
  out = out.replace(/(Bearer\s+)[A-Za-z0-9._\-+/=|]+/gi, '$1REDACTED')
  // Query-string style token leaks (Line docs never put tokens in URLs but
  // third-party tooling sometimes does).
  out = out.replace(/(channel[_-]?access[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(channel[_-]?secret=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(access[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  // Bare base64 / JWT-like blobs of 40+ chars. Line access tokens are always
  // longer than 100 chars in practice; the 40-char floor catches shorter
  // test fixtures while avoiding legitimate IDs (PSIDs, userIds) which are
  // <=40 chars and usually alphanumeric-only (no `+/=`).
  out = out.replace(
    /[A-Za-z0-9+/_\-]{40,}={0,2}/g,
    (match) => {
      // Don't scrub plain hex message ids / user ids (all 0-9a-f and <=40
      // chars — most Line user ids are exactly 33 chars: "U" + 32 hex). The
      // rule above already bounds length at 40 so this branch catches 40+
      // char `[0-9a-f]` digests (SHA) we want to preserve for debugging.
      if (/^[0-9a-f]+$/.test(match) && match.length <= 64) return match
      return 'REDACTED'
    },
  )
  return out
}
